import React from "react";
import { FaChevronRight } from "react-icons/fa";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { Button } from "src/common/components/Button";
import { Field } from "src/common/components/Field";
import { Form, FormCustomContainer } from "src/common/components/Form";
import { GridColumn, GridRow } from "src/common/components/Grid";
import { Input, SyncInput } from "src/common/components/Input";
import { AuthPage } from "src/common/components/Page";
import { PasswordInput } from "src/common/components/PasswordInput";
import { TextRight } from "src/common/components/TextAlign";
import { api } from "src/common/utils/api";
import { getQueryParam } from "src/common/utils/location";
import { AuthType } from "src/features/auth";
import { useCreateGenericError } from "src/features/notifications";

import logo from "src/images/univsemmelweis_logo.png";
import "./SignIn.css";

enum AuthStep {
	Type = "TYPE",
	Password = "PASSWORD",
	Passwordless = "PASSWORDLESS",
	TwoFactorFirst = "2_FACTOR_FIRST",
	TwoFactorSecond = "2_FACTOR_SECOND"
}

export const messages = defineMessages({
	title: {
		id: "form.sign_in",
		defaultMessage: "TIMM"
	},
	help: {
		id: "form.sign_in.help",
		defaultMessage: "Strukturált adatgyűjtés"
	},
	forgottenPasswordLink: {
		id: "form.sign_in.forgotten_password_link",
		defaultMessage: "Elfelejtette a jelszavát?"
	},
	next: {
		id: "form.sign_in.next",
		defaultMessage: "Tovább"
	},
	submit: {
		id: "form.sign_in.submit",
		defaultMessage: "Belépés"
	},
	email: {
		id: "form.sign_in.email",
		defaultMessage: "Email cím"
	},
	password: {
		id: "form.sign_in.password",
		defaultMessage: "Jelszó"
	},
	pin: {
		id: "form.sign_in.pin",
		defaultMessage: "Belépési kód"
	}
});

export const SignIn: React.FunctionComponent = () => {
	const { formatMessage } = useIntl();
	const createGenericError = useCreateGenericError();

	const [isAutoFilled, setAutoFilled] = React.useState(false);
	const [isSubmitted, setSubmitted] = React.useState(false);
	const [isLoading, setLoading] = React.useState(false);
	const [authStep, setAuthStep] = React.useState<AuthStep>(AuthStep.Type);

	const [email, setEmail] = React.useState<string | undefined>(undefined);
	const [password, setPassword] = React.useState<string | undefined>(
		undefined
	);
	const [check, setCheck] = React.useState<string | undefined>();
	const [pin, setPin] = React.useState<string | undefined>();

	const onChangePin = React.useCallback((value: string | undefined) => {
		setPin(value === undefined ? value : value.replace(/\D/g, ""));
	}, []);

	const emailRef = React.useRef(email);
	React.useEffect(() => {
		emailRef.current = email;
	}, [email]);

	React.useEffect(() => {
		const selector = `#${AuthStep.Type}__username`;
		const waitForInput = (): void => {
			const el = document.querySelector(selector);
			if (el === null) {
				window.setTimeout(waitForInput, 50);
			} else {
				let pseudoTestCount = 0;
				const waitForPseudo = (): void => {
					const el = document.querySelector(selector);
					if (el === null) {
						throw new Error();
					}
					if (el.matches("input:-webkit-autofill")) {
						setAutoFilled(true);
					} else {
						pseudoTestCount++;
						if (pseudoTestCount < 10) {
							window.setTimeout(waitForPseudo, 50);
						}
					}
				};
				waitForPseudo();
			}
		};
		waitForInput();
	}, []);

	const submitType = React.useCallback(async () => {
		setSubmitted(true);
		setLoading(true);
		if (isAutoFilled) {
			const initialValue = emailRef.current;
			await new Promise<void>(resolve => {
				let valueTestCount = 0;
				const waitForValue = (): void => {
					if (emailRef.current !== initialValue) {
						resolve();
					} else {
						valueTestCount++;
						if (valueTestCount < 10) {
							window.setTimeout(waitForValue, 50);
						} else {
							resolve();
						}
					}
				};
				waitForValue();
			});
		}
		try {
			const { data } = await api.post<AuthType>("/authflow/type", {
				email: emailRef.current
			});
			if (data === AuthType.EmailPassword) {
				setLoading(false);
				setAuthStep(AuthStep.Password);
			} else if (data === AuthType.Passwordless) {
				const { data } = await api.post<string>(
					"/authflow/passwordlessreq",
					{ email: emailRef.current }
				);
				setLoading(false);
				setAuthStep(AuthStep.Passwordless);
				setCheck(data);
			} else if (data === AuthType.TwoFactor) {
				setLoading(false);
				setAuthStep(AuthStep.TwoFactorFirst);
			}
		} catch {
			setLoading(false);
			createGenericError();
		}
	}, [createGenericError, isAutoFilled]);

	const submitEmailAndPassword = React.useCallback(async () => {
		setSubmitted(true);
		setLoading(true);
		try {
			await api.post("/authflow/login", { email, password });
			setLoading(false);
			redirectToHome();
		} catch {
			setLoading(false);
			createGenericError();
		}
	}, [createGenericError, email, password]);

	const submitPasswordless = React.useCallback(async () => {
		setSubmitted(true);
		setLoading(true);
		try {
			await api.post("/authflow/passwordless", { email, check, pin });
			setLoading(false);
			redirectToHome();
		} catch {
			setLoading(false);
			createGenericError();
		}
	}, [createGenericError, email, check, pin]);

	const submit2FactorFirst = React.useCallback(async () => {
		setSubmitted(true);
		setLoading(true);
		try {
			const { data } = await api.post<string>("/authflow/2factor/first", {
				email,
				password
			});
			setLoading(false);
			setAuthStep(AuthStep.TwoFactorSecond);
			setCheck(data);
		} catch {
			setLoading(false);
			createGenericError();
		}
	}, [createGenericError, email, password]);

	const submit2FactorSecond = React.useCallback(async () => {
		setSubmitted(true);
		setLoading(true);
		try {
			await api.post("/authflow/2factor/second", {
				email,
				password,
				check,
				pin
			});
			setLoading(false);
			redirectToHome();
		} catch {
			setLoading(false);
			createGenericError();
		}
	}, [createGenericError, email, password, check, pin]);

	const onSubmit = React.useCallback(() => {
		if (authStep === AuthStep.Type) {
			return submitType();
		}
		if (authStep === AuthStep.Password) {
			submitEmailAndPassword();
		}
		if (authStep === AuthStep.Passwordless) {
			submitPasswordless();
		}
		if (authStep === AuthStep.TwoFactorFirst) {
			submit2FactorFirst();
		}
		if (authStep === AuthStep.TwoFactorSecond) {
			submit2FactorSecond();
		}
	}, [
		authStep,
		submitType,
		submitEmailAndPassword,
		submitPasswordless,
		submit2FactorFirst,
		submit2FactorSecond
	]);

	return (
		<AuthPage logo={<img src={logo} />}>
			<Form
				className="sign-in__form"
				isSubmitted={isSubmitted}
				onSubmit={onSubmit}
			>
				<h1>
					<FormattedMessage {...messages.title} />
				</h1>
				<h2>
					<FormattedMessage {...messages.help} />
				</h2>
				{authStep === AuthStep.Type && (
					<>
						<GridRow>
							<GridColumn columnSize={12}>
								<Field label={formatMessage(messages.email)}>
									<div className="sign-in__step">
										<Input
											type="email"
											id={`${authStep}__username`}
											name={`${authStep}__username`}
											autoComplete="username"
											value={email}
											onChangeValue={setEmail}
										/>
										<Button
											type="submit"
											disabled={!isAutoFilled && !email}
											isLoading={isLoading}
										>
											<FaChevronRight />
										</Button>
									</div>
								</Field>
							</GridColumn>
						</GridRow>
						{/* we need this hidden password field for autocompletion */}
						<div className="sign-in__hidden-password">
							<PasswordInput
								tabIndex={-1}
								id={`${authStep}__password}`}
								name={`${authStep}__password}`}
								autoComplete="current-password"
								value={password}
								onChangeValue={setPassword}
							/>
						</div>
					</>
				)}
				{authStep === AuthStep.Password && (
					<>
						<GridRow>
							<GridColumn columnSize={12}>
								<Field label={formatMessage(messages.email)}>
									<div className="sign-in__step">
										<Input
											type="email"
											id={`${authStep}__username`}
											name={`${authStep}__username`}
											autoComplete="username"
											value={email}
											onChangeValue={setEmail}
										/>
									</div>
								</Field>
							</GridColumn>
						</GridRow>
						<GridRow>
							<GridColumn columnSize={12}>
								<Field label={formatMessage(messages.password)}>
									<div className="sign-in__step">
										<PasswordInput
											id={`${authStep}__password`}
											name={`${authStep}__password`}
											autoComplete="current-password"
											value={password}
											onChangeValue={setPassword}
										/>

										<Button
											type="submit"
											disabled={!password}
											isLoading={isLoading}
										>
											<FaChevronRight />
										</Button>
									</div>
								</Field>
							</GridColumn>
						</GridRow>
						<GridColumn columnSize={12}>
							<FormCustomContainer>
								<TextRight>
									<Link to="/forgotten-password">
										<FormattedMessage
											{...messages.forgottenPasswordLink}
										/>
									</Link>
								</TextRight>
							</FormCustomContainer>
						</GridColumn>
					</>
				)}
				{authStep === AuthStep.Passwordless && (
					<>
						<GridRow>
							<GridColumn columnSize={12}>
								<Field label={formatMessage(messages.email)}>
									<div className="sign-in__step">
										<Input
											type="email"
											id={`${authStep}__username`}
											name={`${authStep}__username`}
											autoComplete="username"
											value={email}
											onChangeValue={setEmail}
										/>
									</div>
								</Field>
							</GridColumn>
						</GridRow>
						<GridColumn columnSize={12}>
							<Field label={formatMessage(messages.pin)}>
								<div className="sign-in__step">
									<SyncInput
										type="text"
										id={`${authStep}__pin`}
										name={`${authStep}__pin`}
										autoComplete="one-time-code"
										prefix={check}
										value={pin}
										onChangeValue={onChangePin}
									/>
									<Button
										type="submit"
										disabled={!pin}
										isLoading={isLoading}
									>
										<FaChevronRight />
									</Button>
								</div>
							</Field>
						</GridColumn>
					</>
				)}
				{authStep === AuthStep.TwoFactorFirst && (
					<>
						<GridRow>
							<GridColumn columnSize={12}>
								<Field label={formatMessage(messages.email)}>
									<div className="sign-in__step">
										<Input
											type="email"
											id={`${authStep}__username`}
											name={`${authStep}__username`}
											autoComplete="username"
											value={email}
											onChangeValue={setEmail}
										/>
									</div>
								</Field>
							</GridColumn>
						</GridRow>
						<GridRow>
							<GridColumn columnSize={12}>
								<Field label={formatMessage(messages.password)}>
									<div className="sign-in__step">
										<PasswordInput
											id={`${authStep}__password`}
											name={`${authStep}__password`}
											autoComplete="password"
											value={password}
											onChangeValue={setPassword}
										/>
										<Button
											type="submit"
											disabled={!password}
											isLoading={isLoading}
										>
											<FaChevronRight />
										</Button>
									</div>
								</Field>
							</GridColumn>
						</GridRow>
						<GridColumn columnSize={12}>
							<FormCustomContainer>
								<TextRight>
									<Link to="/forgotten-password">
										<FormattedMessage
											{...messages.forgottenPasswordLink}
										/>
									</Link>
								</TextRight>
							</FormCustomContainer>
						</GridColumn>
					</>
				)}
				{authStep === AuthStep.TwoFactorSecond && (
					<>
						<GridRow>
							<GridColumn columnSize={12}>
								<Field label={formatMessage(messages.email)}>
									<div className="sign-in__step">
										<Input
											type="email"
											id={`${authStep}__username`}
											name={`${authStep}__username`}
											autoComplete="username"
											value={email}
											onChangeValue={setEmail}
										/>
									</div>
								</Field>
							</GridColumn>
						</GridRow>
						<GridRow>
							<GridColumn columnSize={12}>
								<Field label={formatMessage(messages.password)}>
									<div className="sign-in__step">
										<PasswordInput
											id={`${authStep}__password`}
											name={`${authStep}__password`}
											autoComplete="password"
											value={password}
											onChangeValue={setPassword}
										/>
									</div>
								</Field>
							</GridColumn>
						</GridRow>
						<GridColumn columnSize={12}>
							<Field label={formatMessage(messages.pin)}>
								<div className="sign-in__step">
									<SyncInput
										type="text"
										id={`${authStep}__pin`}
										name={`${authStep}__pin`}
										autoComplete="one-time-code"
										prefix={check}
										value={pin}
										onChangeValue={onChangePin}
									/>
									<Button
										type="submit"
										disabled={!pin}
										isLoading={isLoading}
									>
										<FaChevronRight />
									</Button>
								</div>
							</Field>
						</GridColumn>
						<GridColumn columnSize={12}>
							<FormCustomContainer>
								<TextRight>
									<Link to="/forgotten-password">
										<FormattedMessage
											{...messages.forgottenPasswordLink}
										/>
									</Link>
								</TextRight>
							</FormCustomContainer>
						</GridColumn>
					</>
				)}
			</Form>
		</AuthPage>
	);
};

function redirectToHome(): void {
	window.location.href = getQueryParam("return-to") || "/";
}
